import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import httpClient from "../../utils/httpClients";
import Chart from "chart.js/auto";

function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [playStats, setPlayStats] = useState(null);
  const [popularSongs, setPopularSongs] = useState([]);
  const [timeFilter, setTimeFilter] = useState("daily");

  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchDashboard();
    fetchPlayStats();
    fetchPopularSongs();

    // Cleanup chart on component unmount
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (timeFilter) {
      fetchPlayStats();
    }
  }, [timeFilter]);

  useEffect(() => {
    if (playStats && playStats.details && playStats.details.length > 0) {
      renderChart();
    }
  }, [playStats]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(
        "artist/analytics/artist-dashboard-data",
      );
      setDashboard(response.data.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayStats = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(
        `artist/analytics/artist-play-count-stats?type=${timeFilter}`,
      );
      setPlayStats(response.data.data);
    } catch (error) {
      console.error("Error fetching play stats data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPopularSongs = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(
        "artist/analytics/artist-top-popular-songs",
      );
      setPopularSongs(response.data.data.topSongs || []);
    } catch (error) {
      console.error("Error fetching popular songs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeFilterChange = (e) => {
    setTimeFilter(e.target.value);
  };

  const renderChart = () => {
    if (!playStats?.details) return;

    const ctx = chartRef.current?.getContext("2d");
    if (!ctx) return;

    // Destroy previous chart instance
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Prepare chart data
    const labels = playStats.details.map((item) => item.date);
    const data = playStats.details.map((item) => item.playCount);

    // Create gradient for chart
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(79, 70, 229, 0.6)");
    gradient.addColorStop(1, "rgba(79, 70, 229, 0.1)");

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Plays",
            data: data,
            borderColor: "#4f46e5",
            backgroundColor: gradient,
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#4f46e5",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            borderColor: "#4f46e5",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              label: function (context) {
                return `Plays: ${context.parsed.y}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
              drawBorder: false,
            },
            ticks: {
              color: "#b3b3b3",
            },
          },
          y: {
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
              drawBorder: false,
            },
            ticks: {
              color: "#b3b3b3",
              callback: function (value) {
                return value.toLocaleString();
              },
            },
            beginAtZero: true,
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
      },
    });
  };

  if (loading && !dashboard) {
    return (
      <div className="content-inner pb-0 container-fluid" id="page_layout">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__items">
              <Link to="/artist/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              Artist Panel
            </li>
          </ul>
        </div>
        <div className="col-12">
          <div className="main__title main__title--page">
            {/* You can add a title here if needed */}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>SALES</h3>
          <div className="stat-value">{dashboard?.sales?.total || 0}</div>
          <div className="stat-change">
            <i className="fas fa-arrow-up" />
            <span>+{dashboard?.sales?.newThisMonth || 0} this month</span>
          </div>
        </div>
        <div className="stat-card">
          <h3>FOLLOWERS</h3>
          <div className="stat-value">{dashboard?.followers?.total || 0}</div>
          <div className="stat-change">
            <i className="fas fa-arrow-up" />
            <span>+{dashboard?.followers?.newThisMonth || 0} this month</span>
          </div>
        </div>
        <div className="stat-card">
          <h3>SONGS</h3>
          <div className="stat-value">{dashboard?.songs?.total || 0}</div>
          <div className="stat-change">
            <i className="fas fa-arrow-up" />
            <span>+{dashboard?.songs?.newThisMonth || 0} this month</span>
          </div>
        </div>
        <div className="stat-card">
          <h3>TOTAL PLAYS</h3>
          <div className="stat-value">{dashboard?.plays?.total || 0}</div>
          <div className="stat-change">
            <i className="fas fa-arrow-up" />
            <span>+{dashboard?.plays?.newThisMonth || 0} this month</span>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        <div className="chart-container">
          <div className="chart-header">
            <h2>PLAYS CHART</h2>
            <div className="time-filter-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="time-filter-trigger"
                onClick={() => setDropdownOpen((o) => !o)}
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
                aria-label="Time filter"
              >
                {timeFilter.toUpperCase()}
              </button>
              {dropdownOpen && (
                <ul
                  className="time-filter-list"
                  role="listbox"
                  aria-label="Time filter options"
                >
                  {["daily", "weekly", "yearly"].map((value) => (
                    <li
                      key={value}
                      role="option"
                      aria-selected={timeFilter === value}
                      className={`time-filter-option ${timeFilter === value ? "time-filter-option--active" : ""}`}
                      onClick={() => {
                        handleTimeFilterChange({ target: { value } });
                        setDropdownOpen(false);
                      }}
                    >
                      {value.toUpperCase()}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <p style={{ color: "#b3b3b3", marginBottom: 20 }}>
            Number of plays per interval
          </p>

          <div
            className="chart-wrapper"
            style={{ height: "400px", position: "relative" }}
          >
            {playStats?.details && playStats.details.length > 0 ? (
              <canvas
                ref={chartRef}
                id="playsChart"
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <div
                className="no-chart-data"
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#b3b3b3",
                }}
              >
                No play data available for the selected period
              </div>
            )}
          </div>
        </div>

        <div className="popular-songs">
          <h2>POPULAR SONGS</h2>
          <div className="table-responsive">
            <table className="songs-table">
              <thead>
                <tr>
                  <th>COVER</th>
                  <th>TITLE</th>
                  <th>PLAYS</th>
                </tr>
              </thead>
              <tbody>
                {popularSongs.length > 0 ? (
                  popularSongs.map((song, index) => (
                    <tr key={song._id || index}>
                      <td>
                        <img
                          src={
                            song.coverImageUrl || "/img/live/songDefault.png"
                          }
                          alt={song.title}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/50x50?text=No+Image";
                          }}
                        />
                      </td>
                      <td>{song.title}</td>
                      <td>{song.playCount?.toLocaleString() || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="no-data">
                      <i className="fas fa-music" />
                      <p>No songs data available</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
